'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { FadeIn } from '@/components/FadeIn'
import { type Locale } from '@/i18n'

interface TOCItem {
  id: string
  text: string
  level: number
}

interface TableOfContentsProps {
  locale?: Locale
}

export function TableOfContents({ locale: propLocale }: TableOfContentsProps) {
  const [tocItems, setTocItems] = useState<TOCItem[]>([])
  const [activeId, setActiveId] = useState<string>('')
  const [mounted, setMounted] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  // Use prop locale or default to 'fr'
  const locale = propLocale || 'fr'

  useEffect(() => {
    setMounted(true)

    // Petit délai pour s'assurer que le contenu MDX est rendu
    const timer = setTimeout(() => {
      // Extraire tous les titres h1, h2, h3, h4 de la page
      const headings = document.querySelectorAll('.prose h1, .prose h2, .prose h3, .prose h4, article h1, article h2, article h3, article h4, h1[id], h2[id], h3[id], h4[id]')
      
      const items: TOCItem[] = Array.from(headings)
        .filter((heading) => {
          // Ignorer le titre principal (h1 dans le header) et les titres dans les sections "More articles"
          const isMainTitle = heading.closest('header') !== null
          const isInMoreArticles = heading.closest('[class*="relative pt-24"], [class*="grid grid-cols-1"]') !== null
          const text = heading.textContent?.trim() || ''
          
          // Ignorer aussi les titres qui sont des doublons du titre principal et les titres TOC
          const isMainTitleDuplicate = text.includes('Guide Complet du Marketing Digital') || 
                                      text.includes('Comprehensive Guide to Digital Marketing')
          const isTOCTitle = text.includes('Table des matières') ||
                            text.includes('Table of Contents') ||
                            text.includes('Inhaltsverzeichnis') ||
                            text.includes('Tabla de contenidos') ||
                            text.includes('Sumário')
          
          return !isMainTitle && !isInMoreArticles && !isMainTitleDuplicate && !isTOCTitle && text !== ''
        })
        .map((heading) => {
          // Créer un ID si il n'y en a pas
          if (!heading.id) {
            heading.id = heading.textContent?.toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '') // Enlever les accents
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/(^-|-$)/g, '') || ''
          }

          return {
            id: heading.id,
            text: heading.textContent || '',
            level: parseInt(heading.tagName.charAt(1))
          }
        })

      console.log('TOC Items found:', items) // Debug
      console.log('Headings found by selector:', headings.length) // Debug
      console.log('Headings elements:', Array.from(headings)) // Debug
      setTocItems(items)

      if (items.length > 0) {
        // Observer pour mettre en surbrillance le titre actif
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setActiveId(entry.target.id)
              }
            })
          },
          { rootMargin: '-80px 0px -80% 0px' }
        )

        headings.forEach((heading) => {
          if (heading.id) observer.observe(heading)
        })

        return () => {
          observer.disconnect()
        }
      }
    }, 1000) // Délai de 1000ms

    return () => {
      clearTimeout(timer)
    }
  }, [])

  if (!mounted || tocItems.length === 0) {
    return (
      <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-6 mb-8 max-w-lg mx-auto text-center">
        <h3 className="text-lg font-semibold text-primary-700 mb-4 flex items-center justify-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
          {locale === 'fr' ? 'Table des matières' :
           locale === 'de' ? 'Inhaltsverzeichnis' :
           locale === 'es' ? 'Tabla de contenidos' :
           locale === 'pt' ? 'Sumário' : 'Table of Contents'}
        </h3>
        <div className="text-sm text-gray-700">
          {locale === 'fr' ? 'Chargement...' :
           locale === 'de' ? 'Wird geladen...' :
           locale === 'es' ? 'Cargando...' :
           locale === 'pt' ? 'Carregando...' : 'Loading...'}
        </div>
      </div>
    )
  }

  return (
    <FadeIn>
      <div className="relative mb-8 max-w-lg mx-auto">
        {/* Bouton dropdown */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-4 flex items-center justify-between text-primary-700 hover:from-primary-100 hover:to-secondary-100 transition-colors"
        >
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            <span className="font-semibold">
              {locale === 'fr' ? 'Table des matières' :
               locale === 'de' ? 'Inhaltsverzeichnis' :
               locale === 'es' ? 'Tabla de contenidos' :
               locale === 'pt' ? 'Sumário' : 'Table of Contents'}
            </span>
          </div>
          <svg
            className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Contenu dropdown */}
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 z-10 max-h-96 overflow-y-auto">
            <nav className="p-4">
              <ul className="space-y-2">
                {tocItems.map((item) => (
                  <li key={item.id} style={{ marginLeft: `${Math.max(0, (item.level - 2)) * 1}rem` }}>
                    <Link
                      href={`#${item.id}`}
                      className={`block text-sm py-2 px-3 rounded-lg transition-colors hover:bg-primary-50 hover:text-primary-600 ${
                        activeId === item.id
                          ? 'text-primary-600 font-medium bg-primary-50'
                          : 'text-gray-600'
                      }`}
                      onClick={(e) => {
                        e.preventDefault()
                        setIsOpen(false) // Fermer le dropdown après clic
                        const element = document.getElementById(item.id)
                        if (element) {
                          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
                          const offsetPosition = elementPosition - 100

                          window.scrollTo({
                            top: offsetPosition,
                            behavior: 'smooth'
                          })
                        }
                      }}
                    >
                      {item.text}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        )}
      </div>
    </FadeIn>
  )
}